import { useCallback, useEffect, useRef, useState } from 'react';
import { qualityControlReportService } from '../services/api';

const DEBOUNCE_MS = 700;
const RETRY_MS = 15000;

const cacheKey = (reportId) => `qcr_pending_${reportId}`;

export const readPendingCache = (reportId) => {
  try {
    const raw = localStorage.getItem(cacheKey(reportId));
    if (!raw) return {};
    return JSON.parse(raw)?.items || {};
  } catch {
    return {};
  }
};

const writePendingCache = (reportId, items) => {
  try {
    if (!items || Object.keys(items).length === 0) localStorage.removeItem(cacheKey(reportId));
    else localStorage.setItem(cacheKey(reportId), JSON.stringify({ cached_at: new Date().toISOString(), items }));
  } catch { /* storage full or unavailable: autosave still works online */ }
};

/**
 * Debounced, per-measurement autosave with offline queue in localStorage.
 */
export function useQcrAutosave(reportId, { onSaved, onConflict } = {}) {
  const [status, setStatus] = useState('idle'); // idle | dirty | saving | saved | offline | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const pendingRef = useRef({});      // key -> payload waiting to be persisted
  const timersRef = useRef({});       // key -> debounce timer
  const inFlightRef = useRef(false);

  const syncPending = useCallback(() => {
    const items = { ...pendingRef.current };
    setPendingCount(Object.keys(items).length);
    writePendingCache(reportId, items);
  }, [reportId]);

  const flushAll = useCallback(async () => {
    const entries = Object.entries(pendingRef.current);
    if (entries.length === 0 || inFlightRef.current) return;
    if (!navigator.onLine) { setStatus('offline'); return; }
    inFlightRef.current = true;
    setStatus('saving');
    try {
      const res = await qualityControlReportService.syncMeasurements(reportId, entries.map(([, v]) => v));
      entries.forEach(([k]) => { delete pendingRef.current[k]; });
      syncPending();
      setLastSavedAt(new Date());
      setStatus(Object.keys(pendingRef.current).length ? 'dirty' : 'saved');
      if (res.data?.conflicts?.length && onConflict) onConflict(res.data.conflicts[0]);
      onSaved?.(res.data);
    } catch (e) {
      setStatus(navigator.onLine ? 'error' : 'offline');
    } finally {
      inFlightRef.current = false;
    }
  }, [reportId, syncPending, onSaved, onConflict]);

  const saveOne = useCallback(async (key, payload) => {
    if (!navigator.onLine) {
      pendingRef.current[key] = payload;
      syncPending();
      setStatus('offline');
      return;
    }
    setStatus('saving');
    try {
      const res = await qualityControlReportService.autosaveMeasurement(reportId, payload);
      delete pendingRef.current[key];
      syncPending();
      setLastSavedAt(new Date());
      setStatus(Object.keys(pendingRef.current).length ? 'dirty' : 'saved');
      onSaved?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'measurement_conflict') {
        delete pendingRef.current[key];
        syncPending();
        setStatus('error');
        onConflict?.({ ...detail, key, incoming: payload });
        return;
      }
      if (e?.response) {
        // server rejected the value (locked report, validation): keep it visible as error
        setStatus('error');
        delete pendingRef.current[key];
        syncPending();
        return;
      }
      pendingRef.current[key] = payload;
      syncPending();
      setStatus(navigator.onLine ? 'error' : 'offline');
    }
  }, [reportId, syncPending, onSaved, onConflict]);

  const queue = useCallback((key, payload, { immediate = false } = {}) => {
    pendingRef.current[key] = payload;
    syncPending();
    setStatus('dirty');
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    if (immediate) {
      saveOne(key, payload);
      return;
    }
    timersRef.current[key] = setTimeout(() => {
      delete timersRef.current[key];
      saveOne(key, pendingRef.current[key] || payload);
    }, DEBOUNCE_MS);
  }, [saveOne, syncPending]);

  const flushKey = useCallback((key) => {
    if (!timersRef.current[key]) return;
    clearTimeout(timersRef.current[key]);
    delete timersRef.current[key];
    const payload = pendingRef.current[key];
    if (payload) saveOne(key, payload);
  }, [saveOne]);

  /** Drop a queued value without saving it (used by Esc in the grid). */
  const cancelKey = useCallback((key) => {
    if (timersRef.current[key]) {
      clearTimeout(timersRef.current[key]);
      delete timersRef.current[key];
    }
    delete pendingRef.current[key];
    syncPending();
    setStatus(Object.keys(pendingRef.current).length ? 'dirty' : 'saved');
  }, [syncPending]);

  const forceSave = useCallback(async (payload) => {
    await qualityControlReportService.autosaveMeasurement(reportId, { ...payload, force: true });
    setLastSavedAt(new Date());
    setStatus('saved');
  }, [reportId]);

  const restoreCached = useCallback(() => {
    const cached = readPendingCache(reportId);
    pendingRef.current = { ...cached };
    setPendingCount(Object.keys(cached).length);
    if (Object.keys(cached).length) setStatus('dirty');
    return cached;
  }, [reportId]);

  // Connection + periodic retry
  useEffect(() => {
    const onOnline = () => { setStatus(Object.keys(pendingRef.current).length ? 'dirty' : 'saved'); flushAll(); };
    const onOffline = () => setStatus('offline');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const t = setInterval(() => { if (Object.keys(pendingRef.current).length) flushAll(); }, RETRY_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(t);
    };
  }, [flushAll]);

  // Warn only when there really are unsynchronized changes
  useEffect(() => {
    const handler = (e) => {
      if (Object.keys(pendingRef.current).length === 0) return;
      e.preventDefault();
      e.returnValue = 'Ci sono modifiche non ancora sincronizzate.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return { status, lastSavedAt, pendingCount, queue, flushKey, cancelKey, flushAll, forceSave, restoreCached, setLastSavedAt, setStatus };
}
